// filter-module.ts

type CardElement = HTMLElement;

type Filter = {
    grade: string;
    subject: string;
};

function getCards(): NodeListOf<CardElement> {
    return document.querySelectorAll('#team .teacher__card');
}

function getFilterButtons(): NodeListOf<HTMLButtonElement> {
    return document.querySelectorAll('#team .filter-btn');
}

function getSubjectOptions(): NodeListOf<HTMLLIElement> {
    return document.querySelectorAll('#team .filter-dropdown__option');
}

function getCardByFilter(filter: Filter): CardElement | null {
    const cards = getCards();
    for (const card of cards) {
        if (
            card.getAttribute('data-grades') === filter.grade &&
            card.getAttribute('data-subjects') === filter.subject
        ) {
            return card;
        }
    }
    return null;
}

function hasCardForFilter(filter: Filter): boolean {
    return !!getCardByFilter(filter);
}

function getFirstAvailableSubject(grade: string): string | null {
    const cards = getCards();
    for (const card of cards) {
        if (card.getAttribute('data-grades') === grade) {
            return card.getAttribute('data-subjects');
        }
    }
    return null;
}

function getFirstAvailableGrade(subject: string): string | null {
    const cards = getCards();
    for (const card of cards) {
        if (card.getAttribute('data-subjects') === subject) {
            return card.getAttribute('data-grades');
        }
    }
    return null;
}

function setActiveCard(card: CardElement | null) {
    const cards = getCards();
    const activeCard = Array.from(cards).find(c => c.classList.contains('active'));

    if (activeCard && activeCard !== card) {
        activeCard.classList.add('fading');
        activeCard.style.opacity = '0';
        activeCard.style.pointerEvents = 'none';
        setTimeout(() => {
            activeCard.classList.remove('active');
            activeCard.classList.remove('fading');
            activeCard.classList.add('disabled');
            activeCard.style.opacity = '';
            activeCard.style.pointerEvents = '';
            if (card) {
                card.classList.remove('disabled');
                card.classList.add('active');
                card.style.opacity = '0';
                setTimeout(() => {
                    card.style.opacity = '1';
                }, 10); // небольшой таймаут для плавности
            }
        }, 400); // длительность анимации в ms
    } else {
        cards.forEach(c => {
            c.classList.remove('active');
            c.classList.add('disabled');
            c.style.opacity = '';
            c.style.pointerEvents = '';
        });
        if (card) {
            card.classList.remove('disabled');
            card.classList.add('active');
            card.style.opacity = '1';
        }
    }
}

function setActiveButton(grade: string) {
    getFilterButtons().forEach(btn => {
        const btnGrade = btn.textContent?.trim() || '';
        if (btnGrade === grade) {
            btn.classList.add('active');
        } else {
            btn.classList.remove('active');
        }
    });
}

function setActiveSubjectOption(subject: string) {
    getSubjectOptions().forEach(option => {
        const optSubject = option.textContent?.trim() || '';
        if (optSubject === subject) {
            option.classList.add('active');
        } else {
            option.classList.remove('active');
        }
    });
}

function updateFilterButtons(subject: string) {
    // Кнопки всегда активны, ничего не делаем
}

function updateSubjectOptions(grade: string) {
    getSubjectOptions().forEach(option => {
        const subject = option.textContent?.trim() || '';
        const hasCard = hasCardForFilter({ grade, subject });
        if (hasCard) {
            option.classList.remove('disabled');
        } else {
            option.classList.add('disabled');
        }
    });
}

function handleFilterChange(filter: Filter) {
    // Проверяем, есть ли карточка для выбранной пары
    let { grade, subject } = filter;
    if (!hasCardForFilter({ grade, subject })) {
        // Если нет карточки для пары, ищем первый доступный subject для grade
        const firstSubject = getFirstAvailableSubject(grade);
        if (firstSubject) {
            subject = firstSubject;
        } else {
            // Если нет ни одного subject для grade, ищем первую доступную карточку вообще
            const firstCard = getCards()[0];
            if (firstCard) {
                grade = firstCard.getAttribute('data-grades') || grade;
                subject = firstCard.getAttribute('data-subjects') || subject;
            }
        }
    }
    // Ставим active на карточку, кнопку и опцию
    const card = getCardByFilter({ grade, subject });
    setActiveCard(card);
    setActiveButton(grade);
    setActiveSubjectOption(subject);
    updateFilterButtons(subject);
    updateSubjectOptions(grade);
}

export function initTeacherFilter() {
    // Проверяем есть ли старый блок команды на странице
    const teamSection = document.querySelector('#team');
    if (!teamSection) {
        return;
    }
    
    // По умолчанию активируем ЕГЭ Математика
    handleFilterChange({ grade: 'ЕГЭ', subject: 'Математика' });

    // Кнопки фильтра по grade
    getFilterButtons().forEach(btn => {
        btn.addEventListener('click', () => {
            const grade = btn.textContent?.trim() || '';
            const selectedSubject = document.querySelector('#team .filter-dropdown__option.active')?.textContent?.trim() || 'Биология';
            handleFilterChange({ grade, subject: selectedSubject });
        });
    });

    // Клики по предметам
    getSubjectOptions().forEach(option => {
        option.addEventListener('click', () => {
            const subject = option.textContent?.trim() || '';
            const activeBtn = document.querySelector('#team .filter-btn.active');
            const grade = activeBtn?.textContent?.trim() || 'ЕГЭ';
            // Если нет карточки для выбранной пары, handleFilterChange сам подберёт первую подходящую
            handleFilterChange({ grade, subject });
        });
    });

    // --- Мобильный кастомный дропдаун ---
    const dropdown = document.querySelector('#team .filter-dropdown');
    const toggle = dropdown?.querySelector('.filter-dropdown__toggle') as HTMLButtonElement;
    const list = dropdown?.querySelector('.filter-dropdown__list') as HTMLUListElement;
    const selectedSpan = dropdown?.querySelector('.filter-dropdown__selected') as HTMLSpanElement;

    function closeDropdown() {
        dropdown?.classList.remove('open');
        toggle?.setAttribute('aria-expanded', 'false');
    }

    function openDropdown() {
        dropdown?.classList.add('open');
        toggle?.setAttribute('aria-expanded', 'true');
    }

    if (toggle && list && selectedSpan && dropdown) {
        // Инициализация выбранного предмета
        const activeOption = list.querySelector('.filter-dropdown__option.active');
        if (activeOption) {
            selectedSpan.textContent = activeOption.textContent;
        }

        // Открытие/закрытие по клику
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            if (dropdown.classList.contains('open')) {
                closeDropdown();
            } else {
                openDropdown();
            }
        });

        // Клик вне дропдауна — закрыть
        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target as Node)) {
                closeDropdown();
            }
        });

        // Выбор предмета
        list.querySelectorAll('.filter-dropdown__option').forEach(option => {
            option.addEventListener('click', () => {
                selectedSpan.textContent = option.textContent;
                closeDropdown();
            });
        });
    }
} 